<template>
  <section
    class="min-h-screen bg-surface-base text-fg-primary text-fg-g-primary print:!bg-transparent print:!text-black"
  >
    <teleport to="head">
      <link :href="settingStore.css_url" rel="stylesheet" type="text/css" />
    </teleport>
    <div class="fixed z-50 bottom-10 mx-8 print:hidden flex flex-col justify-end items-start gap-4">
      <Message
        v-show="hasVenueAppVersionChanged"
        class="w-auto"
        id="venue-version-alert"
        severity="secondary"
        closable
      >
        There's a new version of TurnTable available.
        <a id="refresh-button" class="text-fg-link-primary cursor-pointer" @click="refresh">
          Refresh to get the latest.
        </a>
      </Message>
      <Message
        v-show="isLiveUpdatesPaused"
        class="w-auto"
        id="live-updates-paused-alert"
        severity="warn"
      >
        Live updates are paused. Reconnecting…
      </Message>
    </div>
    <HeaderComponent class="print:hidden" v-show="$route.meta.header !== false && !isFullscreen" />
    <div class="w-full" :class="{ 'px-4 lg:px-16 pb-12': !isSettingsPage }">
      <VenueStripeAlert v-show="!isFullscreen" />
      <VenueSubNav v-show="!isSettingsPage && !$route.meta.usesCustomNavbar" />
      <router-view />
    </div>

    <DevelopmentIndicator />
    <Toast />
  </section>
</template>

<script lang="ts">
import { venueRoutes } from './router/venue-routes'
import { useIntercom } from '@homebaseai/vue3-intercom'
import {
  breakpointsTailwind,
  useBreakpoints,
  useEventBus,
  useTimeoutFn,
  useWebSocket,
} from '@vueuse/core'
import { useFullscreen } from '@vueuse/core'
import { mapStores, storeToRefs } from 'pinia'
import Message from 'primevue/message'
import Toast from 'primevue/toast'
import { defineComponent, ref, watch } from 'vue'

import { guestKey, onVenueMessage } from '@/api/events'
import DevelopmentIndicator from '@/common/components/atoms/DevelopmentIndicator.vue'
import { useFeatureFlags } from '@/common/composables/useFeatureFlags'
import { usePerformanceReservationStore } from '@/modules/performance/stores/PerformanceReservationStore'
import VenueSubNav from '@/modules/venueUi/components/atoms/VenueSubNav.vue'
import VenueStripeAlert from '@/modules/venueUi/components/molecules/VenueStripeAlert.vue'
import HeaderComponent from '@/modules/venueUi/components/organisms/HeaderComponent.vue'
import { useCheckoutStore } from '@/stores/checkout'
import { useSettingStore } from '@/stores/setting'
import { useUserStore } from '@/stores/user'
import loc from '@/utils/loc'

const RECONNECT_DELAY_CAP_MS = 10_000
const LIVE_UPDATES_PAUSED_AFTER_MS = 5_000

function reconnectDelayMs(retried: number): number {
  return Math.min(1000 * 2 ** (retried - 1), RECONNECT_DELAY_CAP_MS) * (0.5 + Math.random() / 2)
}

export default defineComponent({
  name: 'AppVenue',
  components: {
    VenueStripeAlert,
    VenueSubNav,
    Message,
    HeaderComponent,
    DevelopmentIndicator,
    Toast,
  },

  computed: {
    ...mapStores(useSettingStore, useCheckoutStore),
    hasVenueAppVersionChanged(): boolean {
      return this.settingStore.hasVenueAppVersionChanged
    },
    isLargeScreen(): boolean {
      return useBreakpoints(breakpointsTailwind).lg.value
    },
    isOnboardingFlow(): boolean {
      return !!this.$route.meta?.onboarding
    },
    isSettingsPage(): boolean {
      return this.$route.path?.includes('venue/settings')
    },
  },
  setup() {
    //@ts-ignore
    venueRoutes[0].component() // force load the venue chunk
    const { status } = useWebSocket(`wss://${window.location.host}/ws/`, {
      autoReconnect: { delay: reconnectDelayMs },
      onMessage: onVenueMessage,
    })
    const isLiveUpdatesPaused = ref<boolean>(false)
    const pausedAlertTimeout = useTimeoutFn(() => {
      isLiveUpdatesPaused.value = true
    }, LIVE_UPDATES_PAUSED_AFTER_MS)
    watch(status, current => {
      if (current === 'OPEN') {
        pausedAlertTimeout.stop()
        isLiveUpdatesPaused.value = false
      } else if (!pausedAlertTimeout.isPending.value) {
        pausedAlertTimeout.start()
      }
    })
    useEventBus(guestKey).on(usePerformanceReservationStore().onGuestChange)

    const { isIntercomChatEnabled } = useFeatureFlags()
    const userStore = useUserStore()
    const { me } = storeToRefs(userStore)
    if (isIntercomChatEnabled.value && me.value.id) {
      const intercom = useIntercom()
      const bootData: any = {
        app_id: 'pyp7ps5m',
        user_id: String(me.value.id),
        name: me.value.name,
        email: me.value.email,
        created_at: me.value.last_login,
        venue: useSettingStore().venue_slug,
      }

      // Add JWT token for identity verification if available
      if ((me.value as any).intercom_jwt) {
        bootData.user_hash = (me.value as any).intercom_jwt
      }

      intercom.boot(bootData)
    }
    const { isFullscreen } = useFullscreen()
    return { isFullscreen, isLiveUpdatesPaused }
  },
  methods: {
    refresh() {
      loc.reload()
    },
    updateTheme(): void {
      // Load venue-ui theme
      document.body.classList.add('venue-ui')
      // Make sure we don't load the guest-ui theme
      document.body.classList.remove('guest-ui')
    },
  },
  mounted() {
    if (document) {
      const hsScript = document.createElement('script')
      hsScript.src = '//js.hs-scripts.com/22692115.js'
      document.head.appendChild(hsScript)
    }
    this.updateTheme()
  },
  watch: {
    '$route.path'(value: string) {
      if (value.startsWith('/venue')) {
        this.checkoutStore.performance = null
      }
      this.updateTheme()
    },
  },
})
</script>
